// components/VisualisationBDC.tsx
// Visualisation des bons de caisse — même présentation que
// /assurance/visualisation (Safe Care) :
//   critères de recherche -> liste des bons -> clic sur une ligne ->
//   fiche à onglets (Résumé / Échéancier / Frais / Contrat signé).
import React, { useEffect, useMemo, useState } from "react";
import { Button, Descriptions, Form, Input, Select, Space, Spin, Table, Tabs, Tag, Modal } from "antd";
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, FilePdfOutlined } from '@ant-design/icons';
import {
  Typography, Box,
} from "@mui/material";
import axios from "axios";
import { IBonDeCaisse } from "../../../dataTypes/GlobalDataType";
import { Crypter } from "../../../Functions/Security/Crypter";
import Data from '../../../Data/Data.json';
import { useAuthHeader, useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from "../../../Models/Utilisateur";
import { fetchDataTable2, GlobalExportToCSV, GlobalExportToExcel, GlobalExportToJSON, GlobalExportToPDF, GlobalExportToWord } from "../../../utilitaires/globalFunction";
import ExportMenu from "../../ExportMenu";
import formatNumber from "../../../Functions/Traitement/FormatNumber";
import SweetAlert from "sweetalert2";

interface IEcheance {
  periode: number;
  dateImput: string;
  interet: number;
  taxe: number;
  netAPayer: number;
}

const ETAT_COLOR: Record<string, string> = {
  '01': 'orange', '02': 'green', '03': 'red', '04': 'default',
};
const ETAT_LABEL: Record<string, string> = {
  '01': 'En cours', '02': 'Validé', '03': 'Rejeté', '04': 'Échu',
};
const libEtat = (s?: string) => (s ? ETAT_LABEL[s] ?? s : '');
const EtatTag = ({ s }: { s?: string }) => (s ? <Tag color={ETAT_COLOR[s] ?? 'default'}>{libEtat(s)}</Tag> : null);
const fmtDate = (d?: any) => (d ? new Date(d).toLocaleDateString() : '');
const fmtDateTime = (d?: any) => (d ? new Date(d).toLocaleString() : '');

export default function VisualisationBDC() {
  const auth = useAuthUser()
  const authHeader = useAuthHeader()
  const Perss: Utilisateur = JSON.parse(JSON.stringify(auth()))

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [allData, setAllData] = useState<IBonDeCaisse[]>([]);
  const [typesBdc, setTypesBdc] = useState<{ CODBDC: string; LIBELL: string }[]>([]);
  const [selected, setSelected] = useState<IBonDeCaisse | null>(null);
  const [ficheTab, setFicheTab] = useState('resume');

  // Critères
  const [recherche, setRecherche] = useState('');
  const [etat, setEtat] = useState<string | undefined>(undefined);
  const [typBdc, setTypBdc] = useState<string | undefined>(undefined);

  // Fiche
  const [echeancier, setEcheancier] = useState<IEcheance[]>([]);
  const [frais, setFrais] = useState<any[]>([]);
  const [contratB64, setContratB64] = useState<string>('');
  const [contratNom, setContratNom] = useState<string>('');
  const [contratPreview, setContratPreview] = useState(false);

  const totalInteret = echeancier.reduce((acc, r) => acc + r.interet, 0);
  const totalTaxe = echeancier.reduce((acc, r) => acc + r.taxe, 0);
  const totalNet = echeancier.reduce((acc, r) => acc + r.netAPayer, 0);

  // ------------------------------------------------------------------
  // Chargement
  // ------------------------------------------------------------------
  const fetchTypes = async () => {
    try {
      var params = { CodeAgence: Perss.codage, Table: "PARAMBDC", Colonne1: "CODBDC", Colonne2: "LIBELL", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      setTypesBdc(res || []);
    } catch (err) { console.warn('fetchTypes - erreur', err); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      var params = { CodeAgence: Perss.codage, Table: "BONDECAISSE", Colonne1: "NUMCLI", Colonne2: "NOMCLI", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      setAllData(res || []);
    } catch (err) {
      console.error("Erreur de chargement:", err);
      setAllData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); fetchTypes(); }, []);

  // Filtrage local (état / type / recherche libre)
  const filtered = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return allData.filter((b: any) => {
      if (etat && b.CDETAT !== etat) return false;
      if (typBdc && b.TYPBDC !== typBdc) return false;
      if (!q) return true;
      return [b.NUMBDC, b.REFBDC, b.CODSIMUL, b.NUMCLI, b.NOMCLI, b.CPTDEPOT, b.CPTBDC]
        .some((v) => (v ?? '').toString().toLowerCase().includes(q));
    });
  }, [allData, recherche, etat, typBdc]);

  // ------------------------------------------------------------------
  // Fiche : échéancier calculé + frais + contrat
  // ------------------------------------------------------------------
  const joursFreq = (freq: string) => {
    switch ((freq || "").toUpperCase()) {
      case "A": return 360; case "S": return 180; case "T": return 90;
      case "M": return 30; case "B": return 15; case "F": return 60;
      case "H": return 7; case "J": return 1; default: return 30;
    }
  }

  const calculerEcheancier = (row: IBonDeCaisse) => {
    const jours = joursFreq(row.FREQPROV || "");
    const montant = Number(row.MONTANT) || 0;
    const duree = Number(row.DUREE) || 0;
    const montIntBrut = (montant * (Number(row.TXINT) || 0) * duree * jours) / 36000;
    const montProvFreq = Math.round(montIntBrut / duree);
    const montTaxeBrut = ((montProvFreq * duree) * (Number(row.TXTAXE) || 0)) / 100;
    const montTaxeFreq = Math.round(montTaxeBrut / duree);

    let dateEcheance = new Date(row.DATDEBUT || Perss.datjour);
    const list: IEcheance[] = [];
    for (let i = 1; i <= duree; i++) {
      const prochaine = new Date(dateEcheance);
      prochaine.setDate(prochaine.getDate() + jours);
      list.push({
        periode: i,
        dateImput: dateEcheance.toLocaleDateString(),
        interet: montProvFreq,
        taxe: montTaxeFreq,
        netAPayer: montProvFreq - montTaxeFreq,
      });
      dateEcheance = prochaine;
    }
    setEcheancier(list);
  };

  const fetchFrais = async (numbdc?: string) => {
    if (!numbdc) { setFrais([]); return; }
    try {
      var params = { CodeAgence: Perss.codage, Table: "FRAISPRELEVEBDC", Colonne1: "NUMBDC", Colonne2: "TYPEFRAIS", Colagence: "CODAGE", Init: "", choix: 32, colfiltre: "", valfiltre: "" }
      const res = await fetchDataTable2(Data.ApiUrl.AnySearch, params, authHeader());
      setFrais((res || []).filter((f: any) => f.NUMBDC === numbdc));
    } catch (err) { console.warn('fetchFrais - erreur', err); setFrais([]); }
  };

  const fetchContrat = async (numbdc?: string) => {
    setContratB64(''); setContratNom('');
    if (!numbdc) return;
    try {
      const response = await axios({
        method: 'get',
        url: Data.ApiUrl.GetContratBDC,
        headers: {
          'CypherNumBdc': Crypter.encrypt(JSON.stringify(numbdc)),
          'CypherAgence': Crypter.encrypt(JSON.stringify(Perss.codage)),
          'Authorization': authHeader(),
          'Content-Type': 'application/json'
        }
      });
      if (response.data) {
        const res = JSON.parse(Crypter.decrypt(response.data));
        const doc = res?.Result?.Table?.[0];
        if (doc?.PIECEJOINTE) {
          setContratB64(doc.PIECEJOINTE);
          setContratNom(doc.NOMFICHIER || 'contrat.pdf');
        }
      }
    } catch (err) { console.warn('GetContratBDC - erreur', err); }
  };

  const selectRow = (row: IBonDeCaisse) => {
    setSelected(row);
    setFicheTab('resume');
    calculerEcheancier(row);
    fetchFrais(row.NUMBDC);
    fetchContrat(row.NUMBDC);
  };

  const annuler = () => {
    setRecherche(''); setEtat(undefined); setTypBdc(undefined);
    setSelected(null); setEcheancier([]); setFrais([]); setContratB64('');
  };

  // ------------------------------------------------------------------
  // Exports
  // ------------------------------------------------------------------
  const handleExport = (format: any) => {
    const title = "Exportation bon de caisse";
    switch (format) {
      case 'excel': GlobalExportToExcel(filtered, title, "BonDeCaisse"); break;
      case 'pdf': GlobalExportToPDF(filtered, title, "BonDeCaisse"); break;
      case 'word': GlobalExportToWord(filtered, title, "BonDeCaisse"); break;
      case 'csv': GlobalExportToCSV(filtered, title, "BonDeCaisse"); break;
      case 'json': GlobalExportToJSON(filtered, title, "BonDeCaisse"); break;
      default: break;
    }
  };

  // ------------------------------------------------------------------
  // Colonnes
  // ------------------------------------------------------------------
  const columnsBdc: ColumnsType<IBonDeCaisse> = [
    { title: 'N° BDC', dataIndex: 'NUMBDC', width: 110 },
    { title: 'Réf.', dataIndex: 'REFBDC', width: 100 },
    { title: 'Client', dataIndex: 'NOMCLI', ellipsis: true },
    { title: 'Type', dataIndex: 'TYPBDC', width: 70, align: 'center' },
    { title: 'Montant', dataIndex: 'MONTANT', width: 110, align: 'right', render: (v: any) => formatNumber(Number(v) || 0) },
    { title: 'Durée', dataIndex: 'DUREE', width: 60, align: 'right' },
    { title: 'Date début', dataIndex: 'DATDEBUT', width: 95, render: fmtDate },
    { title: 'Date fin', dataIndex: 'DATFIN', width: 95, render: fmtDate },
    { title: 'État', dataIndex: 'CDETAT', width: 85, align: 'center', render: (s: string) => <EtatTag s={s} /> },
    { title: 'Crée par', dataIndex: 'UTISAI', width: 85 },
  ];

  const columnsEch: ColumnsType<IEcheance> = [
    { title: 'Période', dataIndex: 'periode', width: 70, align: 'center' },
    { title: 'Date imputation', dataIndex: 'dateImput', width: 110 },
    { title: 'Intérêt', dataIndex: 'interet', width: 120, align: 'right', render: (v: number) => formatNumber(v) },
    { title: 'Taxe', dataIndex: 'taxe', width: 100, align: 'right', render: (v: number) => formatNumber(v) },
    { title: 'Net à payer', dataIndex: 'netAPayer', width: 120, align: 'right', render: (v: number) => formatNumber(v) },
  ];

  const columnsFrais: ColumnsType<any> = [
    { title: 'Type', dataIndex: 'TYPEFRAIS', width: 120 },
    { title: 'Montant', dataIndex: 'MTNFRAIS', width: 110, align: 'right', render: (v: any) => formatNumber(Number(v) || 0) },
    { title: 'Compte débité', dataIndex: 'NUMCPT', width: 160 },
    { title: 'Grille', dataIndex: 'CODTARIF', width: 140 },
    { title: 'Palier', dataIndex: 'NUMPALIER', width: 60, align: 'center' },
    { title: 'Réf. écriture', dataIndex: 'REFECP', width: 100 },
    { title: 'Date', dataIndex: 'DATCRE', width: 100, render: fmtDate },
    { title: 'Par', dataIndex: 'UTISAI', width: 80 },
  ];

  // ------------------------------------------------------------------
  // Onglets de la fiche
  // ------------------------------------------------------------------
  const tabs = selected ? [
    {
      key: 'resume',
      label: 'Résumé',
      children: (
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }}>
          <Descriptions.Item label="N° BDC">{selected.NUMBDC}</Descriptions.Item>
          <Descriptions.Item label="Référence">{selected.REFBDC}</Descriptions.Item>
          <Descriptions.Item label="Code simul">{selected.CODSIMUL}</Descriptions.Item>
          <Descriptions.Item label="Client">{selected.NUMCLI} - {selected.NOMCLI}</Descriptions.Item>
          <Descriptions.Item label="État"><EtatTag s={selected.CDETAT} /></Descriptions.Item>
          <Descriptions.Item label="Type BDC">{selected.TYPBDC} {typesBdc.find(t => t.CODBDC === selected.TYPBDC)?.LIBELL ? `- ${typesBdc.find(t => t.CODBDC === selected.TYPBDC)?.LIBELL}` : ''}</Descriptions.Item>
          <Descriptions.Item label="Type imputation">{selected.TYPIMPUTINT === 'PRE' ? 'Précompté' : selected.TYPIMPUTINT === 'POS' ? 'Postcompté' : selected.TYPIMPUTINT}</Descriptions.Item>
          <Descriptions.Item label="Fréquence">{selected.FREQPROV}</Descriptions.Item>
          <Descriptions.Item label="Durée">{selected.DUREE}</Descriptions.Item>
          <Descriptions.Item label="Montant">{formatNumber(Number(selected.MONTANT) || 0)}</Descriptions.Item>
          <Descriptions.Item label="Taux intérêt">{selected.TXINT} %</Descriptions.Item>
          <Descriptions.Item label="Taux taxe">{selected.TXTAXE} %</Descriptions.Item>
          <Descriptions.Item label="Date début">{fmtDate(selected.DATDEBUT)}</Descriptions.Item>
          <Descriptions.Item label="Date fin">{fmtDate(selected.DATFIN)}</Descriptions.Item>
          <Descriptions.Item label="Intérêts bruts">{formatNumber(Number(selected.MTNINT) || 0)}</Descriptions.Item>
          <Descriptions.Item label="Taxe retenue">{formatNumber(Number(selected.MTNTAXE) || 0)}</Descriptions.Item>
          <Descriptions.Item label="Net à payer">{formatNumber(Number(selected.MTNNETAPAYER) || 0)}</Descriptions.Item>
          <Descriptions.Item label="Compte dépôt">{selected.CPTDEPOT}</Descriptions.Item>
          <Descriptions.Item label="Compte BDC">{selected.CPTBDC}</Descriptions.Item>
          <Descriptions.Item label="Compte intérêts">{selected.CPTINTCLI}</Descriptions.Item>
          <Descriptions.Item label="Code opération">{selected.CODOPE}</Descriptions.Item>
          <Descriptions.Item label="Créé par">{selected.UTISAI}</Descriptions.Item>
          <Descriptions.Item label="Créé le">{fmtDateTime(selected.DATCRE)}</Descriptions.Item>
          <Descriptions.Item label="Validé par">{selected.UTIVAL}</Descriptions.Item>
          <Descriptions.Item label="Validé le">{fmtDateTime(selected.DATVAL)}</Descriptions.Item>
          <Descriptions.Item label="Agence">{selected.CODAGE}</Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'echeancier',
      label: `Échéancier (${echeancier.length})`,
      children: (
        <>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Intérêts bruts {formatNumber(totalInteret)} — Taxe {formatNumber(totalTaxe)} — Net {formatNumber(totalNet)}
          </Typography>
          <Table size="small" rowKey="periode" columns={columnsEch} dataSource={echeancier} pagination={false} scroll={{ y: 260 }} />
        </>
      ),
    },
    {
      key: 'frais',
      label: `Frais (${frais.length})`,
      children: <Table size="small" rowKey={(r: any) => String(r.IDFRAIS ?? Math.random())} columns={columnsFrais} dataSource={frais} pagination={false} />,
    },
    {
      key: 'contrat',
      label: 'Contrat signé',
      children: contratB64 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            onClick={() => setContratPreview(true)}
            style={{ width: 92, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, border: '1px solid #d9d9d9', background: '#fff5f5', cursor: 'pointer', position: 'relative' }}
          >
            <FilePdfOutlined style={{ fontSize: 28, color: '#d4380d' }} />
            <EyeOutlined style={{ position: 'absolute', top: 4, left: 4, color: 'var(--darkColor, #116599)', fontSize: 12 }} />
          </div>
          <div>
            <b>{contratNom}</b>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>Cliquez pour visualiser le contrat signé</div>
          </div>
        </div>
      ) : (
        <Typography variant="body2" color="textSecondary">Aucun contrat signé chargé pour ce bon.</Typography>
      ),
    },
  ] : [];

  return (
    <div className='tab'>
      <Typography align='center' className='formTitle'> <b> VISUALISATION BON DE CAISSE </b> </Typography>
      <Spin spinning={loading} size='large' style={{ color: '#116599' }}>
        <Form autoComplete="off" labelAlign='left' form={form} style={{ minWidth: 'auto', maxWidth: 'auto' }}>
          <div className='bG'>
            <legend><Typography color={"#116599"}>Actions</Typography></legend>
            <Form.Item shouldUpdate className='bouttong'>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button size='large' className='bt_add' onClick={() => { fetchData(); }} type="ghost" htmlType="button" block  >
                  Actualiser
                </Button>
                <ExportMenu disabled={filtered.length === 0} onExport={handleExport} />
                <Button size='large' onClick={annuler} className='bt_add' type="ghost" htmlType="reset" block>
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className='clientP'>
            <div>
              <Tabs size='small' type="card" defaultActiveKey="1" items={[{
                key: '1',
                label: <span id='Tab'><EyeOutlined /> Visualisation</span>,
                children: (
                  <div className='tabItem' style={{ height: '780px', display: 'block' }}>
                    <div style={{ width: '85%', height: '100%', overflowY: 'auto', overflowX: 'hidden', paddingRight: 8 }}>

                      <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2 }}>
                        <legend>Critères de recherche</legend>
                        <Space wrap>
                          <Input.Search
                            allowClear
                            placeholder="N° BDC, client, compte, référence, simul..."
                            style={{ width: 300 }}
                            value={recherche}
                            onChange={(e) => setRecherche(e.target.value)}
                          />
                          <Select
                            allowClear
                            placeholder="Tous les états"
                            style={{ minWidth: 150 }}
                            value={etat}
                            onChange={(v) => { setEtat(v); setSelected(null); }}
                            options={Object.entries(ETAT_LABEL).map(([v, l]) => ({ value: v, label: l }))}
                          />
                          <Select
                            allowClear
                            placeholder="Tous les types"
                            style={{ minWidth: 220 }}
                            value={typBdc}
                            onChange={(v) => { setTypBdc(v); setSelected(null); }}
                            options={typesBdc.map((t) => ({ value: t.CODBDC, label: `${t.CODBDC} - ${t.LIBELL}` }))}
                          />
                        </Space>
                      </Box>

                      <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2 }}>
                        <legend>Bons de caisse ({filtered.length})</legend>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>Sélectionnez une ligne pour afficher la fiche</Typography>
                        <Table
                          size="small"
                          rowKey={(r: IBonDeCaisse) => String(r.NUMBDC)}
                          columns={columnsBdc}
                          dataSource={filtered}
                          rowSelection={{ type: 'radio', selectedRowKeys: selected ? [String(selected.NUMBDC)] : [], onChange: (_, rows) => { if (rows[0]) selectRow(rows[0]); } }}
                          onRow={(r) => ({ onClick: () => selectRow(r), style: { cursor: 'pointer' } })}
                          pagination={{ pageSize: 8, showSizeChanger: false }}
                          scroll={{ x: 900 }}
                        />
                      </Box>

                      {selected && (
                        <Box fontStyle="inherit" className="border border-left" sx={{ p: 2 }}>
                          <legend>
                            Fiche bon de caisse {selected.NUMBDC} — {selected.NOMCLI} <EtatTag s={selected.CDETAT} />
                          </legend>
                          <Tabs size="small" type="card" activeKey={ficheTab} onChange={setFicheTab} items={tabs} />
                        </Box>
                      )}
                    </div>
                  </div>
                ),
              }]} />
            </div>
            <div>
            </div>
          </div>
        </Form>
      </Spin>

      {/* Prévisualisation du contrat signé */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: 'var(--darkblue)' }}>
            <EyeOutlined style={{ color: 'var(--darkColor)' }} />
            {contratNom || 'Aperçu du contrat'}
          </div>
        }
        open={contratPreview}
        onCancel={() => setContratPreview(false)}
        footer={null}
        width="90%"
        centered
        zIndex={2100}
        style={{ maxWidth: '1400px' }}
        bodyStyle={{ maxHeight: 'calc(100vh - 160px)', overflow: 'auto', padding: 16 }}
      >
        {contratB64 && (
          <iframe
            src={contratB64}
            title="Aperçu contrat"
            style={{ width: '100%', height: '75vh', border: '1px solid var(--colorOpaque030)', borderRadius: 10 }}
          />
        )}
      </Modal>
    </div>
  );
}
